import { memo, useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { GitBranch } from 'lucide-react';
import { CanvasGroupBox } from '../entities/group/GroupBox';
import { getHandler } from '../entities/registry';
import { useCanvasStores, useCanvasEntities, useCanvasGroups, useCanvasTopLevelOrder, useCanvasLayers, useCanvasRotation } from '../stores/CanvasContext';
import { useHiddenLayerIds } from '../hooks/useHiddenLayerIds';
import { useGroupBoundsMap } from '../hooks/useGroupBoundsMap';
import { computeCollapseHidden } from '../collapseUtils';
import { useDependencyExplorerHighlight } from '../hooks/useDependencyExplorerHighlight';
import { ResourceChipOverlay } from './ResourceChipOverlay';
import { getAttachedResourceIds } from '../entityTypes';
import type { GroupBounds, CanvasGroup } from '../types';
import type { CanvasEntity } from '../entityTypes';
import { getViewportBounds, type ViewportBounds } from '../lib/viewportBounds';
import { EntityDataProvider } from '../stores/EntityDataContext';
import { useEntityEventDelegation } from '../hooks/useEntityEventDelegation';

type VisibleElement =
  | { type: 'entity'; id: string; layerId: string; zOrder: number; data: CanvasEntity }
  | { type: 'group'; id: string; layerId: string; zOrder: number; group: CanvasGroup; bounds: GroupBounds };

/** Padding in **screen-pixels** added around the viewport so elements render before scrolling into view.
 *  Divided by zoom at runtime to get the canvas-coordinate padding. */
const VIEWPORT_PADDING_SCREEN = 200;
/** Quantisation bucket in **screen-pixels**. Divided by zoom so that viewport changes smaller than
 *  this screen-distance don't trigger a re-render. */
const VIEWPORT_GRANULARITY_SCREEN = 100;

// ── Progressive rendering constants ─────────────────────────────────
/** Target render time in ms. setRenderTick triggers a synchronous React render
 *  inside the rAF callback, so we can measure actual reconciliation + commit
 *  cost with performance.now() around it. */
const TARGET_RENDER_MS = 12;
/** Minimum elements per batch – always make forward progress. */
const MIN_BATCH = 2;
/** Maximum elements per batch – cap to avoid huge frames on very fast devices. */
const MAX_BATCH = 200;

function CanvasEntityLayerInner({ resourceDropTargetId }: { resourceDropTargetId: string | null }) {
  const { t } = useTranslation();
  const { uiStore, panZoomStore, containerRef, visibleEntityIdsRef, debugBatchRef } = useCanvasStores();
  const entities = useCanvasEntities();
  const rotation = useCanvasRotation();
  const groups = useCanvasGroups();
  const topLevelEntityOrder = useCanvasTopLevelOrder();
  const layers = useCanvasLayers();
  const hiddenLayerIds = useHiddenLayerIds();
  const groupBoundsMap = useGroupBoundsMap();

  const { hiddenNodeIds, hiddenGroupIds } = useMemo(
    () => computeCollapseHidden(groups), [groups],
  );

  const entityLayerMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of entities) {
      if (e.kind === 'task' || e.kind === 'textblock' || e.kind === 'textlabel' || e.kind === 'sprite' || e.kind === 'image' || e.kind === 'blocknode' || e.kind === 'shape' || e.kind === 'adr-trigger') {
        map.set(e.id, e.layerId);
      }
    }
    return map;
  }, [entities]);

  const groupMap = useMemo(
    () => new Map(groups.map(g => [g.id, g])),
    [groups],
  );

  const layerOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    for (let i = 0; i < layers.length; i++) map.set(layers[i].id, i);
    return map;
  }, [layers]);

  const getLayerOrder = useCallback((layerId: string) => {
    return layerOrderMap.get(layerId) ?? 0;
  }, [layerOrderMap]);

  const zOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    let z = 0;

    function walk(id: string) {
      const group = groupMap.get(id);
      if (group) {
        if (hiddenGroupIds.has(id)) return;
        if (group.layerId && hiddenLayerIds.has(group.layerId)) return;
        map.set(id, z++);
        for (const childId of group.memberEntityIds) walk(childId);
      } else {
        if (hiddenNodeIds.has(id)) return;
        const layerId = entityLayerMap.get(id);
        if (layerId && hiddenLayerIds.has(layerId)) return;
        map.set(id, z++);
      }
    }

    for (const id of topLevelEntityOrder) walk(id);
    return map;
  }, [topLevelEntityOrder, groupMap, hiddenNodeIds, hiddenGroupIds, hiddenLayerIds, entityLayerMap]);

  // ── Viewport culling ──────────────────────────────────────────────
  const [viewportBounds, setViewportBounds] = useState<ViewportBounds>(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const h = typeof window !== 'undefined' ? window.innerHeight : 1080;
    return {
      left: -VIEWPORT_PADDING_SCREEN,
      top: -VIEWPORT_PADDING_SCREEN,
      right: w + VIEWPORT_PADDING_SCREEN,
      bottom: h + VIEWPORT_PADDING_SCREEN,
    };
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let prevKey = '';
    let rafId = 0;

    function computeAndSetBounds() {
      const pz = panZoomStore.getSnapshot().context;
      const vp = getViewportBounds(container!, pz, rotation);
      const G = VIEWPORT_GRANULARITY_SCREEN / pz.zoom;
      const P = VIEWPORT_PADDING_SCREEN / pz.zoom;
      const key = `${Math.round(vp.left / G)}:${Math.round(vp.top / G)}:${Math.round(vp.right / G)}:${Math.round(vp.bottom / G)}`;
      if (key !== prevKey) {
        prevKey = key;
        setViewportBounds({
          left: vp.left - P,
          top: vp.top - P,
          right: vp.right + P,
          bottom: vp.bottom + P,
        });
      }
    }

    // Initial compute synchronously (no pending DOM writes yet)
    computeAndSetBounds();

    // On pan/zoom changes, defer getBoundingClientRect to next frame
    // to avoid forced reflow after CanvasTransformLayer writes el.style.transform
    const sub = panZoomStore.subscribe(() => {
      if (rafId) return; // already scheduled
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        computeAndSetBounds();
      });
    });
    return () => { sub.unsubscribe(); cancelAnimationFrame(rafId); };
  }, [panZoomStore, containerRef, rotation]);

  const depHighlight = useDependencyExplorerHighlight();

  const sortedVisibleElements = useMemo(() => {
    const elements: VisibleElement[] = [];

    for (const e of entities) {
      if (!getHandler(e.kind).hasOwnRect) continue;
      if (e.kind === 'task' && hiddenNodeIds.has(e.id)) continue;
      if (e.layerId && hiddenLayerIds.has(e.layerId)) continue;
      const handler = getHandler(e.kind);
      if (!handler.Component) continue;
      const z = zOrderMap.get(e.id);
      if (z === undefined) continue;
      if (e.x + e.width < viewportBounds.left || e.x > viewportBounds.right ||
          e.y + e.height < viewportBounds.top || e.y > viewportBounds.bottom) {
        continue;
      }
      elements.push({ type: 'entity', id: e.id, layerId: e.layerId, zOrder: z, data: e });
    }

    for (const g of groups) {
      if (hiddenGroupIds.has(g.id)) continue;
      if (g.layerId && hiddenLayerIds.has(g.layerId)) continue;
      const bounds = groupBoundsMap.get(g.id);
      if (!bounds) continue;
      const z = zOrderMap.get(g.id);
      if (z === undefined) continue;
      if (bounds.x + bounds.width < viewportBounds.left || bounds.x > viewportBounds.right ||
          bounds.y + bounds.height < viewportBounds.top || bounds.y > viewportBounds.bottom) {
        continue;
      }
      elements.push({ type: 'group', id: g.id, layerId: g.layerId, zOrder: z, group: g, bounds });
    }

    elements.sort((a, b) => {
      const lo = getLayerOrder(a.layerId) - getLayerOrder(b.layerId);
      if (lo !== 0) return lo;
      return a.zOrder - b.zOrder;
    });

    return elements;
  }, [entities, groups, hiddenNodeIds, hiddenGroupIds, hiddenLayerIds, zOrderMap, getLayerOrder, groupBoundsMap, viewportBounds]);

  // Write visible IDs to ref so agents can read them imperatively (O(1)).
  // Always reflects the FULL viewport, regardless of progressive rendering state.
  useEffect(() => {
    visibleEntityIdsRef.current = new Set(sortedVisibleElements.map(el => el.id));
  }, [sortedVisibleElements, visibleEntityIdsRef]);

  // ── Adaptive progressive rendering ─────────────────────────────────
  // Measures actual frame-to-frame time and scales batch size proportionally
  // to stay under TARGET_FRAME_MS. This automatically accounts for ALL overhead
  // (React reconciliation, browser paint, compositing, GC, …) without a fixed budget.
  const committedIdsRef = useRef(new Set<string>());
  const rafRef = useRef(0);
  const lastBatchSizeRef = useRef(MIN_BATCH);
  const [renderTick, setRenderTick] = useState(0);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);

    const committed = committedIdsRef.current;
    const targetIds = new Set(sortedVisibleElements.map(el => el.id));

    // Remove elements that left the viewport
    for (const id of committed) {
      if (!targetIds.has(id)) committed.delete(id);
    }

    // Collect new elements not yet committed
    const pending: string[] = [];
    for (const el of sortedVisibleElements) {
      if (!committed.has(el.id)) pending.push(el.id);
    }

    if (pending.length === 0) {
      debugBatchRef.current = { batchSize: 0, pending: 0 };
      return;
    }

    // Schedule batches via RAF – never block the current handler.
    // Exponential ramp-up: 2→4→8→16→…→200. Each rAF callback doubles
    // the batch size. This avoids the rAF timing pitfall where frame-to-frame
    // measurement can't distinguish "fast" from "idle" (v-sync floor ~16ms).
    let idx = 0;
    let currentBatchSize = lastBatchSizeRef.current;

    function nextBatch() {
      const batchSize = Math.min(currentBatchSize, pending.length - idx);
      const end = idx + batchSize;
      for (let i = idx; i < end; i++) committed.add(pending[i]);
      idx = end;

      debugBatchRef.current = { batchSize, pending: pending.length - idx };

      // setRenderTick triggers a synchronous React render (reconciliation +
      // commit + DOM mutations). Measure that cost directly — this avoids the
      // rAF v-sync floor problem where frame-to-frame time is always ≥16ms.
      const renderStart = performance.now();
      setRenderTick(t => t + 1);
      const renderTime = performance.now() - renderStart;

      if (renderTime > TARGET_RENDER_MS) {
        // Over budget — scale down proportionally
        currentBatchSize = Math.max(MIN_BATCH, Math.round(batchSize * TARGET_RENDER_MS / renderTime));
      } else {
        // Under budget — double for next frame
        currentBatchSize = Math.min(MAX_BATCH, currentBatchSize * 2);
      }
      lastBatchSizeRef.current = currentBatchSize;

      if (idx < pending.length) {
        rafRef.current = requestAnimationFrame(nextBatch);
      }
    }

    rafRef.current = requestAnimationFrame(nextBatch);

    return () => cancelAnimationFrame(rafRef.current);
  }, [sortedVisibleElements]);

  // Filter to only render committed elements (preserves sort order)
  const elementsToRender = useMemo(
    () => {
      const committed = committedIdsRef.current;
      return sortedVisibleElements.filter(el => committed.has(el.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedVisibleElements, renderTick],
  );

  const showOverlays = depHighlight.isActive && depHighlight.highlightedEntityIds !== null;
  const isProgressiveLoading = elementsToRender.length < sortedVisibleElements.length;

  return (
    <EntityDataProvider>
      <DelegatedEntityContainer elementsToRender={elementsToRender} />

      {/* Resource chip overlays */}
      {elementsToRender.map(el => {
        if (el.type === 'group') return null;
        const resourceIds = getAttachedResourceIds(el.data);
        if (resourceIds.length === 0) return null;
        return (
          <ResourceChipOverlay
            key={`res-${el.id}`}
            entityId={el.id}
            entityX={el.data.x}
            entityY={el.data.y}
            entityWidth={el.data.width}
            resourceIds={resourceIds}
          />
        );
      })}

      {/* Drop-target highlight for resource drag */}
      {resourceDropTargetId && (() => {
        const target = entities.find(e => e.id === resourceDropTargetId);
        if (!target) return null;
        return (
          <div
            key={`drop-hl-${target.id}`}
            style={{
              position: 'absolute',
              left: target.x - 2,
              top: target.y - 2,
              width: target.width + 4,
              height: target.height + 4,
              border: '2px solid #6366f1',
              borderRadius: 8,
              backgroundColor: 'rgba(99,102,241,0.08)',
              pointerEvents: 'none',
              zIndex: 4,
            }}
          />
        );
      })()}

      {depHighlight.isActive && elementsToRender.map(el => {
        if (el.type === 'group') return null;
        const isFocused = el.id === depHighlight.focusId;
        return (
          <button
            key={`dep-toggle-${el.id}`}
            data-dep-explorer-toggle={el.id}
            onClick={(e) => {
              e.stopPropagation();
              if (isFocused) {
                uiStore.send({ type: 'depExplorer.setFocus', entityId: null });
              } else {
                uiStore.send({ type: 'depExplorer.setFocus', entityId: el.id });
              }
            }}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: el.data.x + el.data.width - 10,
              top: el.data.y - 6,
              width: 16,
              height: 16,
              borderRadius: 8,
              border: `1.5px solid ${isFocused ? '#f59e0b' : '#d1d5db'}`,
              backgroundColor: isFocused ? '#fffbeb' : '#fff',
              color: isFocused ? '#b45309' : '#9ca3af',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
              zIndex: 10,
              transition: 'border-color 0.15s, background-color 0.15s, color 0.15s',
            }}
            title={isFocused ? t('canvasLayer.hideDeps') : t('canvasLayer.showDeps')}
          >
            <GitBranch size={9} />
          </button>
        );
      })}

      {showOverlays && elementsToRender.map(el => {
        const elId = el.id;
        const isFocus = elId === depHighlight.focusId;
        let isAncestor = depHighlight.highlightedEntityIds!.has(elId);

        // Groups: don't dim if any member is an ancestor
        if (!isAncestor && el.type === 'group') {
          for (const mid of el.group.memberEntityIds) {
            if (depHighlight.highlightedEntityIds!.has(mid)) {
              isAncestor = true;
              break;
            }
          }
        }

        if (isAncestor && !isFocus) return null;

        let ox: number, oy: number, ow: number, oh: number;
        if (el.type === 'group') {
          ox = el.bounds.x; oy = el.bounds.y; ow = el.bounds.width; oh = el.bounds.height;
        } else {
          ox = el.data.x; oy = el.data.y; ow = el.data.width; oh = el.data.height;
        }

        if (isFocus) {
          return (
            <div
              key={`dep-focus-${elId}`}
              data-dep-overlay={elId}
              className="dep-explorer-focus-ring"
              style={{
                position: 'absolute',
                left: ox - 3,
                top: oy - 3,
                width: ow + 6,
                height: oh + 6,
                pointerEvents: 'none',
              }}
            />
          );
        }

        // Not an ancestor → dim overlay
        return (
          <div
            key={`dep-dim-${elId}`}
            data-dep-overlay={elId}
            style={{
              position: 'absolute',
              left: ox,
              top: oy,
              width: ow,
              height: oh,
              backgroundColor: 'rgba(249,250,251,0.72)',
              borderRadius: 6,
              pointerEvents: 'none',
              transition: 'opacity 0.2s',
            }}
          />
        );
      })}

      {/* Progressive loading indicator – portaled out of the CSS-transformed layer */}
      {isProgressiveLoading && containerRef.current && createPortal(
        <div
          style={{
            position: 'absolute',
            bottom: 48,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: '#fff',
            padding: '5px 14px',
            borderRadius: 6,
            fontSize: 11,
            fontFamily: 'monospace',
            zIndex: 60,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: 80,
              height: 4,
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.round((elementsToRender.length / sortedVisibleElements.length) * 100)}%`,
                height: '100%',
                backgroundColor: '#60a5fa',
                borderRadius: 2,
                transition: 'width 0.1s',
              }}
            />
          </div>
          <span>
            {elementsToRender.length}/{sortedVisibleElements.length}
          </span>
        </div>,
        containerRef.current,
      )}
    </EntityDataProvider>
  );
}

/** Inner component that lives inside EntityDataProvider so the delegation hook can use useEntityData(). */
function DelegatedEntityContainer({ elementsToRender }: { elementsToRender: VisibleElement[] }) {
  const delegated = useEntityEventDelegation();
  return (
    <div
      onPointerDown={delegated.onPointerDown}
      onDoubleClick={delegated.onDoubleClick}
      onContextMenu={delegated.onContextMenu}
      style={{ display: 'contents' }}
    >
      {elementsToRender.map(el => {
        if (el.type === 'group') {
          return <CanvasGroupBox key={el.id} group={el.group} bounds={el.bounds} />;
        }
        const handler = getHandler(el.data.kind);
        const C = handler.Component!;
        return <C key={el.id} entity={el.data} />;
      })}
    </div>
  );
}

export const CanvasEntityLayer = memo(CanvasEntityLayerInner) as React.NamedExoticComponent<{ resourceDropTargetId: string | null }>;
